import { FileText, Image, File, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface Attachment {
  key: string;
  fileName: string;
  mimeType: string;
  size: number;
}

interface CommentAttachmentsProps {
  attachments: Attachment[];
}

export const CommentAttachments = ({
  attachments,
}: CommentAttachmentsProps) => {
  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith("image/")) {
      return <Image className="h-4 w-4 text-blue-600" />;
    } else if (mimeType.includes("pdf")) {
      return <FileText className="h-4 w-4 text-red-600" />;
    }
    return <File className="h-4 w-4 text-gray-600" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const fileUrl = `${import.meta.env.VITE_BUCKET_URL}${attachment.key}`;
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      const fileUrl = `${import.meta.env.VITE_BUCKET_URL}${attachment.key}`;
      window.open(fileUrl, "_blank");
    }
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-gray-500 font-medium">Anexos:</p>
      <div className="space-y-1">
        {attachments.map((attachment, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-2 bg-white rounded border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {getFileIcon(attachment.mimeType)}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">
                  {attachment.fileName}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(attachment.size)}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDownload(attachment)}
              className="h-7 w-7 p-0 cursor-pointer"
              title="Baixar arquivo"
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
